import { useState } from 'react'
import styled from 'styled-components'
import { CustomProvider, DateRangePicker } from 'rsuite'
const { afterToday } = DateRangePicker
import ptBR from 'rsuite/locales/pt_BR'
import { Box, Tooltip } from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import {
  AutoCompleteWrapper,
  ChartWithNoteWrapper,
  CustomChartLegend,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
  MixedChartWrapper,
} from '../../components'
import useViewport from '../../hooks/useViewport'
import filterDrawerHelper from '../../utils/filterDrawer'
import { RequestService } from '../../services/RequestService'
import {
  DistributionFatigueProvider,
  useDistributionFatigue,
} from '../../contexts/DistributionFatigueContext'
import { useCustomLegend } from '../../hooks'
import { useTranslation } from "react-i18next"

const Container = styled(Box)`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
`

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #f1f3f5;
  padding: 24px 44px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ChartContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
`

const FiltersTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const ReportDistributionFatigueWrapper = () => {
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const {
    data,
    loading,
    selectedUser,
    onSelectUser,
    dateRange,
    onDateRangeChange,
    selectedFilterOptions,
    filterOptions,
    loadingFilters,
    onSelectFilter,
    inputValue,
    onInputValueChange,
    graphMessage,
    labels,
  } = useDistributionFatigue()
  const { width } = useViewport()
  const { t } = useTranslation('common')
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper
  const legends = [
    { label: t('reports.distribution_fatigue.header.colaborator'), color: '#E27837' },
    { label: t('reports.distribution_fatigue.header.benchmark'), color: '#4CB7BA' },
  ]

  const initalLegendState = {
    [t('reports.distribution_fatigue.header.colaborator')]: true,
    [t('reports.distribution_fatigue.header.benchmark')]: true,
  }
  const { activeSeries, visibleSeries, toggleSeries, defineMax } =
    useCustomLegend({
      initalLegendState,
      data: data?.map(serie => {
        const type = serie.type === 'column' ? t('reports.distribution_fatigue.header.colaborator') : t('reports.distribution_fatigue.header.benchmark')
        return { ...serie, type, nType: serie.type }
      }),
      activeSeriesHandle: s => {
        return {
          name: s.name,
          data: s.data,
          color: s.color,
          type: s.nType,
        }
      },
    })

  return (
    <Container>
      <Body>
        <Title>
          <span style={{ fontSize: 20, fontWeight: 600 }}>
          {t('reports.distribution_fatigue.title')}
          </span>
          <Tooltip
            title={t('reports.distribution_fatigue.tooltip')}
            placement='right'
            arrow
          >
            <HelpOutline fontSize='24' />
          </Tooltip>
        </Title>
        <ChartContainer>
          <FiltersTagsContainer>
            <FiltersContainer>
              <div style={{ display: 'flex', flex: 1 }}>
                <AutoCompleteWrapper
                  onLoad={() => RequestService.getUsers({ visibility: 'reports' })}
                  value={selectedUser}
                  onChangeValue={onSelectUser}
                  inputValue={inputValue}
                  onInputValueChange={onInputValueChange}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 16,
                }}
              >
                <CustomProvider locale={ptBR}>
                  <DateRangePicker
                    defaultCalendarValue={dateRange}
                    value={dateRange}
                    onChange={event => onDateRangeChange(event)}
                    showOneCalendar
                    shouldDisableDate={afterToday()}
                    character=' - '
                    placement='bottom'
                    placeholder={t('reports.distribution_fatigue.placeholder')}
                    ranges={[]}
                    cleanable={false}
                  />
                </CustomProvider>
                <Filter onClick={() => setOpenFilterDrawer(true)} />
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            })?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <GroupedFilterTags
                  tags={selectedFilterOptionsList({
                    selectedFilterOptions,
                  })}
                  onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                  maxTagsToShow={getMaxTagsToShow({ width })}
                />
              </div>
            )}
          </FiltersTagsContainer>

          <ChartWithNoteWrapper
            totalHours={graphMessage?.totalHours}
            totalUsers={graphMessage?.totalUsers}
          >
            {!loading && (
              <MixedChartWrapper
                data={activeSeries}
                labels={labels}
                legendProps={{ show: false }}
                colors={data.map(s => s.color)}
                yProps={{
                  ...defineMax(),
                }}
              />
            )}
            <CustomChartLegend
              visibleSeries={visibleSeries}
              toggleSeries={toggleSeries}
              legends={legends}
            />
          </ChartWithNoteWrapper>
        </ChartContainer>
      </Body>

      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={onSelectFilter}
        loading={loadingFilters}
        options={filterOptions}
        withShift
        withPosition
        withTeam
      />
    </Container>
  )
}

const ReportDistributionFatigue = () => {
  return (
    <DistributionFatigueProvider>
      <ReportDistributionFatigueWrapper />
    </DistributionFatigueProvider>
  )
}

export default ReportDistributionFatigue
